import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Pagination from '../../../components/ui/Pagination';
import RequestStatusBadge from '../../../components/request-builder/RequestStatusBadge';
import { formatRequestAmount, normalizeRequestStatus } from '../../../components/request-builder/requestBuilderUtils';
import repositoryContainer from '../../../services/RepositoryContainer';
import RequestOrderDetailsModal from './RequestOrderDetailsModal';
import { useNotifications } from '../../../components/context/NotificationContext';
import { needsPriceFinalization } from '../../../utils/pricingUtils';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'inprogress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

const formatDate = (value, options = { dateStyle: 'medium' }) => {
  if (!value) return '';
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en-GH', options).format(date);
};

const getItemSummary = (request) => {
  const items = request?.items || [];
  if (items.length === 0) return 'No items';
  if (items.length === 1) return items[0].name;
  return `${items.length} items`;
};

const getTotalLabel = (request) => (
  `${needsPriceFinalization(request) ? 'From ' : ''}${formatRequestAmount(request?.totalAmount)}`
);

const RequestOrdersView = () => {
  const { workflowRefreshVersion } = useNotifications();
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadRequests = async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    if (!silent) setError('');
    try {
      const raw = await repositoryContainer.bookingRequest.listAdmin();
      const data = raw?.response ?? raw;
      const normalizedRequests = Array.isArray(data) ? data : [];
      setRequests(normalizedRequests);
      setSelectedRequest((current) => {
        if (!current) return current;
        const refreshedRequest = normalizedRequests.find((request) => request.id === current.id);
        return refreshedRequest ? { ...current, ...refreshedRequest } : current;
      });
    } catch (loadError) {
      if (!silent) {
        setRequests([]);
        setError(loadError?.data?.message || 'Could not load request orders. Please try again.');
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    if (workflowRefreshVersion > 0) loadRequests({ silent: true });
  }, [workflowRefreshVersion]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, status]);

  const handleRequestUpdated = async (updatedRequest) => {
    if (!updatedRequest?.id) return;
    setRequests((current) => current.map((request) => (
      request.id === updatedRequest.id ? { ...request, ...updatedRequest } : request
    )));
    setSelectedRequest((current) => (
      current?.id === updatedRequest.id ? { ...current, ...updatedRequest } : current
    ));
    await loadRequests({ silent: true });
  };

  const filteredRequests = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return requests
      .filter((request) => status === 'all' || normalizeRequestStatus(request.status) === status)
      .filter((request) => {
        if (!query) return true;
        return [
          request.referenceCode,
          request.clientName,
          request.clientEmail,
          request.clientPhone,
          ...(request.items || []).map((item) => item.name),
        ].some((value) => value?.toString().toLowerCase().includes(query));
      })
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [requests, searchQuery, status]);

  const totalPages = Math.ceil(filteredRequests.length / PAGE_SIZE);
  const visibleRequests = filteredRequests.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="mb-6 rounded-xl border border-border bg-card p-4 md:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="grid flex-1 gap-4 md:grid-cols-2">
            <Input
              type="search"
              label="Search requests"
              placeholder="Reference, customer, phone, or item..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={status}
              onChange={setStatus}
              placeholder="Filter by status"
            />
          </div>
          <div className="flex gap-2">
            {(searchQuery || status !== 'all') && (
              <Button
                type="button"
                variant="outline"
                iconName="RotateCcw"
                onClick={() => { setSearchQuery(''); setStatus('all'); }}
              >
                Reset
              </Button>
            )}
            <Button type="button" variant="outline" iconName="RefreshCw" onClick={() => loadRequests()} loading={isLoading}>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-xl border border-border bg-card p-12 text-center">
          <Icon name="Loader2" size={34} className="mx-auto animate-spin text-primary" />
          <p className="mt-3 text-sm text-muted-foreground">Loading request orders...</p>
        </div>
      ) : error ? (
        <div className="rounded-xl border border-destructive/30 bg-card p-10 text-center">
          <Icon name="CircleAlert" size={34} className="mx-auto text-destructive" />
          <p className="mt-3 font-medium text-foreground">Request inbox unavailable</p>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          <Button type="button" className="mt-5" onClick={() => loadRequests()}>Try Again</Button>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-12 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Icon name="Inbox" size={28} />
          </div>
          <h3 className="mt-4 font-heading text-xl font-semibold text-foreground">
            {requests.length === 0 ? 'No request orders yet' : 'No matching requests'}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {requests.length === 0
              ? 'Customer quote requests will appear here when submitted.'
              : 'Try changing the search or status filter.'}
          </p>
        </div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-xl border border-border bg-card lg:block">
            <div className="max-h-[calc(100vh-390px)] overflow-auto custom-scrollbar">
              <table className="w-full">
                <thead className="sticky top-0 z-10 border-b border-border bg-muted">
                  <tr>
                    {['Reference', 'Customer', 'Items', 'Schedule', 'Total', 'Status', 'Submitted', 'Actions'].map((heading) => (
                      <th key={heading} className="whitespace-nowrap px-4 py-4 text-left text-sm font-semibold text-foreground">
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRequests.map((request) => (
                    <tr key={request.id} className="border-b border-border transition-colors hover:bg-muted/50">
                      <td className="px-4 py-4 font-mono text-xs font-semibold text-primary">{request.referenceCode}</td>
                      <td className="px-4 py-4">
                        <p className="font-medium text-foreground">{request.clientName}</p>
                        <p className="text-xs text-muted-foreground">{request.clientPhone}</p>
                      </td>
                      <td className="px-4 py-4 text-sm text-foreground">{getItemSummary(request)}</td>
                      <td className="px-4 py-4 text-sm text-foreground">
                        {request.requestedDate ? (
                          <>
                            <p>{formatDate(request.requestedDate)}</p>
                            <p className="text-xs text-muted-foreground">{request.requestedTime}</p>
                          </>
                        ) : <span className="text-muted-foreground">Not required</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 font-semibold text-foreground">
                        {getTotalLabel(request)}
                      </td>
                      <td className="px-4 py-4"><RequestStatusBadge status={request.status} showIcon={false} /></td>
                      <td className="whitespace-nowrap px-4 py-4 text-sm text-muted-foreground">
                        {formatDate(request.createdAt)}
                      </td>
                      <td className="px-4 py-4">
                        <Button type="button" variant="ghost" size="sm" iconName="Eye" onClick={() => setSelectedRequest(request)}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-4 lg:hidden">
            {visibleRequests.map((request) => (
              <article key={request.id} className="rounded-xl border border-border bg-card p-4 font-caption">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-caption font-semibold text-foreground text-base mb-1">{request.clientName}</h3>
                    <p className="text-sm text-muted-foreground">{getItemSummary(request)}</p>
                    <p className="mt-1 font-mono text-[11px] text-primary">{request.referenceCode}</p>
                  </div>
                  <RequestStatusBadge status={request.status} showIcon={false} />
                </div>

                <div className="mb-3 flex items-center gap-1 text-sm text-muted-foreground">
                  <Icon name="Calendar" size={16} />
                  <span className="whitespace-nowrap">{formatDate(request.createdAt)}</span>
                </div>

                <div className="flex items-center justify-between border-t border-border pt-3 text-sm">
                  <span className="text-muted-foreground">Total</span>
                  <span className="font-semibold text-foreground">{getTotalLabel(request)}</span>
                </div>

                <Button type="button" variant="outline" size="sm" fullWidth className="mt-3" iconName="Eye" onClick={() => setSelectedRequest(request)}>
                  View Details
                </Button>
              </article>
            ))}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            totalCount={filteredRequests.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            isLoading={isLoading}
          />
        </>
      )}

      {selectedRequest && (
        <RequestOrderDetailsModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onRequestUpdated={handleRequestUpdated}
        />
      )}
    </>
  );
};

export default RequestOrdersView;
